import { Component, ElementRef, Inject, OnInit, ViewChild } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { ID } from '@gh/common-shared-data';
import { AutocompleteLoadMoreEvent, NotificationService, STYLES_DIALOG_LAYOUT, WaitingService } from '@gh/core-ui';
import { BulkReassignLeadsDto, LeadRestService, OfficeRestService } from '@gh/builder-shared-data';
import { sortByStringField } from '@gh/core-util';
import { of$ } from '@gh/rx';
import { map$ } from '@gh/rx/operators';
import { Office } from '../../../builder-shared/switch-to/resources/office.model';
import { LeadListBusinessService, UserDetailsShort } from '../../resources/lead-list-business.service';
import { focusSelectSearchInput, matchesSelectSearch } from '../../utils/lead-select-search';

@Component({
  standalone: false,
  selector: 'gh-bulk-reassign-leads-dialog',
  templateUrl: 'bulk-reassign-leads-dialog.component.html',
  styleUrls: ['bulk-reassign-leads-dialog.component.scss'],
  styles: [STYLES_DIALOG_LAYOUT],
})
export class BulkReassignLeadsDialogComponent implements OnInit {
  leadIds: ID[] = [];
  sourceOfficeId?: ID;
  sourceSalespersonIds: string[] = [];
  offices: Office[] = [];
  salesPersons: UserDetailsShort[] = [];

  selectedOffice: Office | null = null;
  selectedOfficeId?: ID;
  selectedSalesPersonIds: ID[] = [];
  useRoundRobin = false;
  salesPersonSearch = '';

  @ViewChild('salesPersonSearchInput') salesPersonSearchInput?: ElementRef<HTMLInputElement>;

  get isSingleLeadReassign(): boolean {
    return this.leadIds.length === 1;
  }

  constructor(
    @Inject(MAT_DIALOG_DATA) private data: any,
    private dialogRef: MatDialogRef<BulkReassignLeadsDialogComponent>,
    private leadRest: LeadRestService,
    private officeRest: OfficeRestService,
    private businessService: LeadListBusinessService,
    private notificationService: NotificationService,
    private waiting: WaitingService
  ) {
    this.leadIds = data.leadIds || data.contactIds || [];
    this.sourceOfficeId = data.sourceOfficeId;
    this.sourceSalespersonIds = data.sourceSalespersonIds || [];
    this.offices = data.offices || [];
  }

  ngOnInit(): void {
    this.loadActiveFranchiseOffices();
  }

  private loadActiveFranchiseOffices(): void {
    if (this.data.masterZoneId) {
      this.officeRest
        .findAllByMasterZone(this.data.masterZoneId, ['ACTIVE'], ['FRANCHISE'])
        .pipe(
          this.waiting.operator(),
          map$((list) => sortByStringField(list, 'name'))
        )
        .subscribe({
          next: (list) => {
            this.offices = list;
          },
          error: (err) => {
            this.notificationService.processAll(err);
          },
        });
    } else {
      this.businessService
        .findAllActiveOfficesAcrossMasterZones()
        .pipe(
          this.waiting.operator(),
          map$((list) => sortByStringField(list, 'name'))
        )
        .subscribe({
          next: (list) => {
            this.offices = list;
          },
          error: (err) => {
            this.notificationService.processAll(err);
          },
        });
    }
  }

  private lastSalesPersonSelection: ID[] = [];

  loadOfficeOptions(event: AutocompleteLoadMoreEvent): void {
    const query = (event.query.filter || '').toLowerCase();
    event.load(of$(query
      ? this.offices.filter((office) => (office.name || '').toLowerCase().includes(query))
      : this.offices));
  }

  onOfficeSelected(office: Office): void {
    const officeId = office?.id;
    if (officeId === this.selectedOfficeId) {
      return;
    }
    this.selectedOffice = office || null;
    this.selectedOfficeId = officeId;
    this.onOfficeChange();
  }

  onOfficeChange(): void {
    this.selectedSalesPersonIds = [];
    this.lastSalesPersonSelection = [];
    this.salesPersons = [];
    this.salesPersonSearch = '';
    this.useRoundRobin = false;

    if (this.selectedOfficeId) {
      this.businessService.findSalesPersonsByName({ from: 0, to: 1000 }, {
        officeIds: [this.selectedOfficeId],
        showInactive: false,
      }).pipe(this.waiting.operator()).subscribe(res => {
        // Exclude the 'All' option if it comes from the API
        this.salesPersons = res.filter(sp => sp.id !== '');
        if (this.salesPersons.length === 1) {
          this.selectedSalesPersonIds = [this.salesPersons[0].id];
          this.lastSalesPersonSelection = [...this.selectedSalesPersonIds];
        }
      });
    }
  }

  onSalesPersonIdsSelected(): void {
    if (this.isSingleLeadReassign && this.selectedSalesPersonIds.length > 1) {
      const newlySelected = this.selectedSalesPersonIds.find(
        (id) => !this.lastSalesPersonSelection.map(String).includes(String(id)),
      );
      this.selectedSalesPersonIds = newlySelected != null
        ? [newlySelected]
        : [this.selectedSalesPersonIds[this.selectedSalesPersonIds.length - 1]];
    }
    this.lastSalesPersonSelection = [...this.selectedSalesPersonIds];
  }

  isSaveDisabled(): boolean {
    if (!this.selectedOfficeId || this.selectedSalesPersonIds.length === 0) {
      return true;
    }
    // UC-2: Disable Save when target is the same office AND all selected target salespersons
    // are the same as the sources (a no-op reassignment).
    if (this.isSameOfficeAndSameSalesperson()) {
      return true;
    }
    return false;
  }

  /**
   * Returns true when the target office equals the source office AND
   * the target salesperson set is identical to the source salesperson set.
   */
  private isSameOfficeAndSameSalesperson(): boolean {
    if (!this.selectedOfficeId || !this.sourceOfficeId) {
      return false;
    }
    const sameOffice = String(this.selectedOfficeId) === String(this.sourceOfficeId);
    if (!sameOffice) {
      return false;
    }
    if (this.sourceSalespersonIds.length === 0) {
      return false;
    }
    const targetIds = [...this.selectedSalesPersonIds].map(String).sort();
    const sourceIds = [...this.sourceSalespersonIds].map(String).sort();
    return targetIds.length === sourceIds.length &&
      targetIds.every((id, i) => id === sourceIds[i]);
  }

  get showPartialCrossOfficeWarning(): boolean {
    if (!this.selectedOfficeId || !this.sourceOfficeId) {
      return false;
    }
    return String(this.selectedOfficeId) !== String(this.sourceOfficeId);
  }

  onSave(): void {
    // UC-2: Final guard – same office + same salesperson means no-op, reject.
    if (this.isSameOfficeAndSameSalesperson()) {
      this.notificationService.warning('msg_bulk_reassign_same_office_salesperson_error');
      return;
    }

    const request: BulkReassignLeadsDto = {
      leadIds: this.leadIds,
      targetOfficeId: this.selectedOfficeId!,
      targetSalespersonIds: this.selectedSalesPersonIds,
      useRoundRobin: this.isSingleLeadReassign ? false : this.useRoundRobin,
    };

    this.leadRest.bulkReassign(request)
      .pipe(this.waiting.operator())
      .subscribe((res: any) => {
        if (res && res.async) {
          this.notificationService.success('msg_bulk_reassign_leads_task_added');
        } else {
          this.notificationService.success('msg_bulk_reassign_leads_success');
        }
        this.dialogRef.close(true);
      }, (err) => {
        this.notificationService.processAll(err);
      });
  }

  onCancel(): void {
    this.dialogRef.close();
  }

  isSalesPersonOptionVisible(sp: UserDetailsShort): boolean {
    return matchesSelectSearch(this.salesPersonSearch, sp.name);
  }

  get hasNoMatchingSalesPersons(): boolean {
    return !!this.salesPersonSearch.trim()
      && this.salesPersons.length > 0
      && !this.salesPersons.some((sp) => this.isSalesPersonOptionVisible(sp));
  }

  onSalesPersonSelectOpened(opened: boolean): void {
    this.onSelectSearchOpened(opened, this.salesPersonSearchInput);
  }

  onSalesPersonSearch(event: Event): void {
    this.salesPersonSearch = (event.target as HTMLInputElement).value;
  }

  private onSelectSearchOpened(
    opened: boolean,
    input?: ElementRef<HTMLInputElement>,
  ): void {
    if (opened) {
      focusSelectSearchInput(input);
      return;
    }
    this.salesPersonSearch = '';
  }
}
